import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTracker } from "../../context/TrackerContext";

// Shared in-context "Track" button for a horse / trainer / jockey. Renders the
// right state off the shared TrackerContext:
//   logged-out  → link to /login ("log in to track")
//   not tracked → "＋ Track"  (click adds)
//   tracked     → "✓ Tracking" (click removes)
//   cap reached → inline "Tracker full" hint on the 409
//
// `variant="compact"` (default) is the small pill used on dense rows like
// Today's Card; `variant="full"` is the larger button for the Explorer pages.

export default function TrackButton({
  entityType,
  entityId,
  entityName,
  variant = "compact",
  className = "",
}) {
  const { user } = useAuth();
  const { ready, isTracked, track, untrack } = useTracker();
  const [busy, setBusy] = useState(false);
  const [capped, setCapped] = useState(false);

  if (entityId == null) return null;

  const compact = variant !== "full";
  const tracked = isTracked(entityType, entityId);

  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: compact ? 3 : 5,
    fontFamily: "inherit",
    fontSize: compact ? 11 : 13,
    lineHeight: 1,
    padding: compact ? "3px 7px" : "7px 12px",
    borderRadius: 999,
    border: "1px solid",
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: "transparent",
    transition: "background 0.12s, color 0.12s, border-color 0.12s",
  };

  // Logged-out: a plain link to /login, styled as the pill. No JS, cmd-clickable.
  if (!user) {
    return (
      <Link
        to="/login"
        title="Log in to track"
        className={className}
        style={{
          ...base,
          color: "var(--pencil, #6B6560)",
          borderColor: "var(--pencil, #6B6560)",
          textDecoration: "none",
        }}
      >
        <span aria-hidden>＋</span>
        {!compact && <span>Track</span>}
      </Link>
    );
  }

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || !ready) return;
    setCapped(false);
    setBusy(true);
    try {
      if (tracked) {
        await untrack(entityType, entityId);
      } else {
        await track({
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName,
        });
      }
    } catch (err) {
      // 409 limit_reached from the server → show the cap hint.
      const detail = err?.detail;
      const isCap =
        (typeof detail === "object" && detail?.error === "limit_reached") ||
        /limit/i.test(err?.message || "");
      if (isCap) setCapped(true);
    } finally {
      setBusy(false);
    }
  };

  if (capped) {
    return (
      <span
        className={className}
        title="You've reached your tracker limit — manage it on the Tracker page."
        style={{ ...base, cursor: "default", color: "var(--terracotta, #C67B4E)", borderColor: "var(--terracotta, #C67B4E)" }}
      >
        Tracker full ·{" "}
        <Link to="/tracker" style={{ color: "inherit", textDecoration: "underline" }}>
          manage
        </Link>
      </span>
    );
  }

  const trackedStyle = {
    ...base,
    color: "#fff",
    background: "var(--turf, #4A6B3A)",
    borderColor: "var(--turf, #4A6B3A)",
  };
  const untrackedStyle = {
    ...base,
    color: "var(--terracotta, #C67B4E)",
    borderColor: "var(--terracotta, #C67B4E)",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={tracked}
      aria-label={tracked ? `Stop tracking ${entityName}` : `Track ${entityName}`}
      title={tracked ? `Stop tracking ${entityName}` : `Track ${entityName}`}
      className={className}
      style={{
        ...(tracked ? trackedStyle : untrackedStyle),
        opacity: busy ? 0.6 : 1,
      }}
    >
      <span aria-hidden>{tracked ? "✓" : "＋"}</span>
      {(!compact || tracked) && <span>{tracked ? "Tracking" : "Track"}</span>}
    </button>
  );
}
